import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBubble, useSkeletonMinDisplay } from '@/shared/ui';
import { CompletedChatView } from '../../CompletedChatView';
import { FailedChatView } from '../../FailedChatView';
import { InitialChatView } from '../../InitialChatView';
import { TokenLimitExceededView } from '../../TokenLimitExceededView';
import { FileDropArea } from './components';
import { useCreateFromPdf } from './hooks';

type Props = {
  mode: 'create' | 'edit';
};

/**
 * PDFから事業情報を読み取るAI。
 * 作成画面・編集画面共通で使用する。
 */
export const CreateFromPdfMode: FC<Props> = ({ mode }) => {
  const { t } = useTranslation();
  const {
    viewStatus,
    selectedFile,
    extractionResult,
    handleFileDrop,
    handleFileRemove,
    handleAnalyze,
    handleRetry,
  } = useCreateFromPdf();

  const showSkeleton = useSkeletonMinDisplay(
    viewStatus === 'uploading' || viewStatus === 'scanning' || viewStatus === 'analyzing',
  );

  if (viewStatus === 'tokenLimitExceeded') {
    return <TokenLimitExceededView />;
  }

  if (viewStatus === 'error') {
    return <FailedChatView buttonText={t('common.actions.retry')} onButtonClick={handleRetry} />;
  }

  if (viewStatus === 'idle' || viewStatus === 'fileSelected') {
    return (
      <FileDropArea
        selectedFile={selectedFile}
        onFileDrop={handleFileDrop}
        onFileRemove={handleFileRemove}
        onAnalyze={handleAnalyze}
        mode={mode}
      />
    );
  }

  return (
    <div className="space-y-4">
      <MessageBubble
        message={t('ai.pdf-upload.analyzing-message', {
          fileName: selectedFile?.name ?? '',
        })}
        position="right"
      />
      {showSkeleton ? (
        <InitialChatView />
      ) : (
        viewStatus === 'completed' && (
          <CompletedChatView
            description={t('ai.pdf-upload.completed-message')}
            completion={extractionResult?.chat ?? ''}
          />
        )
      )}
    </div>
  );
};
